// ─────────────────────────────────────────────────────────────────────────────
// Payments.js (contractor)
// Contractor subscription and billing page.
//
// SECTIONS:
//   1. Current Plan — trial status, days remaining, upgrade CTA
//   2. Plan Details — what's included
//   3. Billing — monthly/annual toggle, pricing (Stripe integration later)
//   4. Payment History — placeholder until Stripe connected
//   5. Connect Stripe / GoCardless — for sending payment requests to customers
//
// STRIPE INTEGRATION: When ready, replace the "coming soon" buttons with
// Stripe Checkout or Stripe Customer Portal redirect.
// GOCARDLESS INTEGRATION: Add GoCardless Connect flow in the connect section.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Sidebar from '../components/Sidebar'
import TrialBanner from '../components/TrialBanner'

export default function Payments({ session }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [billingCycle, setBillingCycle] = useState('monthly') // 'monthly' | 'annual'

  // ── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => { fetchCompany() }, [])

  const fetchCompany = async () => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', session.user.id)
      .single()

    if (profile?.company_id) {
      const { data } = await supabase
        .from('companies')
        .select('*')
        .eq('id', profile.company_id)
        .single()
      setCompany(data)
    }
    setLoading(false)
  }

  // ── Subscription helpers ───────────────────────────────────────────────────
  const getTrialInfo = () => {
    if (!company) return null
    const now = new Date()
    const trialEnd = company.trial_ends_at ? new Date(company.trial_ends_at) : null
    const daysLeft = trialEnd ? Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)) : null

    if (company.plan_status === 'active') {
      return { status: 'active', label: 'Active', daysLeft: null, color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' }
    }
    if (trialEnd && daysLeft > 0) {
      return { status: 'trial', label: 'Free Trial', daysLeft, color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE', urgent: daysLeft <= 3 }
    }
    return { status: 'expired', label: 'Trial Expired', daysLeft: 0, color: '#BE123C', bg: '#FFF1F2', border: '#FECDD3' }
  }

  // ── Plan features ──────────────────────────────────────────────────────────
  // TO ADD FEATURES: Add items to this array
  const planFeatures = [
    'Unlimited quotes',
    'Unlimited customers',
    'Unlimited invoices',
    'PDF generation & email',
    'Payment requests to customers',
    'Company branding on quotes & invoices',
    'VAT number on quotes & invoices',
    'Quote & invoice status tracking',
    'Customer management',
    'Stripe & GoCardless payments',
  ]

  // ── Pricing ───────────────────────────────────────────────────────────────
  // TO SET PRICES: Update these values when Stripe is configured
  const pricing = {
    monthly: { amount: '£29', period: '/month', note: 'Billed monthly' },
    annual:  { amount: '£23', period: '/month', note: 'Billed annually — save £72/year' },
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  const cardStyle = {
    background: 'white', borderRadius: '12px',
    border: '1px solid #EAECF0', padding: '32px',
    marginBottom: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  }

  const sectionTitle = {
    fontSize: '15px', fontWeight: '700', color: '#0F172A',
    margin: '0 0 24px', paddingBottom: '16px',
    borderBottom: '1px solid #F1F5F9',
  }

  const trialInfo = getTrialInfo()
  const price = pricing[billingCycle]

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', minHeight: '100vh',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      background: '#F7F8FC',
    }}>
      <Sidebar session={session} />
      

      <div style={{ marginLeft: '240px', flex: 1, padding: '36px 40px' }}>

        {/* ── Page Header ── */}
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '800', color: '#0F172A', margin: '0 0 4px', letterSpacing: '-0.4px' }}>
            Payments & Subscription
          </h1>
          <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
            Manage your QuoteFlow subscription and connect payment methods
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#94A3B8', padding: '48px' }}>Loading...</div>
        ) : (
          <div style={{ maxWidth: '720px' }}>

       

            {/* ── 1. Current Plan Status ────────────────────────────────────
                Shows trial countdown or active subscription status.
                Urgent warning shown when 3 or fewer days remain.
            ─────────────────────────────────────────────────────────────── */}
            {trialInfo && (
              <div style={{
                ...cardStyle,
                background: trialInfo.urgent ? '#FFF1F2' : trialInfo.bg,
                border: `1px solid ${trialInfo.urgent ? '#FECDD3' : trialInfo.border}`,
                padding: '24px 28px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <span style={{
                        padding: '4px 12px', borderRadius: '999px', fontSize: '12px',
                        fontWeight: '700', background: 'white',
                        color: trialInfo.urgent ? '#BE123C' : trialInfo.color,
                        border: `1px solid ${trialInfo.urgent ? '#FECDD3' : trialInfo.border}`,
                      }}>{trialInfo.label}</span>
                      {trialInfo.status === 'trial' && (
                        <span style={{ fontSize: '22px', fontWeight: '800', color: trialInfo.urgent ? '#BE123C' : trialInfo.color, letterSpacing: '-0.5px' }}>
                          {trialInfo.daysLeft} day{trialInfo.daysLeft !== 1 ? 's' : ''} remaining
                        </span>
                      )}
                      {trialInfo.status === 'active' && (
                        <span style={{ fontSize: '15px', fontWeight: '700', color: '#15803D' }}>
                          Your subscription is active
                        </span>
                      )}
                    </div>
                    {trialInfo.status === 'trial' && (
                      <p style={{ fontSize: '13px', color: trialInfo.urgent ? '#BE123C' : trialInfo.color, margin: 0, opacity: 0.85 }}>
                        {trialInfo.urgent
                          ? 'Your trial is ending very soon. Upgrade now to avoid losing access.'
                          : `Your free trial ends on ${new Date(company.trial_ends_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.`
                        }
                      </p>
                    )}
                    {trialInfo.status === 'expired' && (
                      <p style={{ fontSize: '13px', color: '#BE123C', margin: 0 }}>
                        Your free trial has expired. Upgrade to continue using QuoteFlow.
                      </p>
                    )}
                    {trialInfo.status === 'active' && (
                      <p style={{ fontSize: '13px', color: '#15803D', margin: 0, opacity: 0.8 }}>
                        Next billing date: managed via Stripe
                      </p>
                    )}
                  </div>
                  {trialInfo.status !== 'active' && (
                    <button style={{
                      padding: '10px 20px', borderRadius: '9px', fontSize: '13px',
                      fontWeight: '700', border: 'none', cursor: 'not-allowed',
                      background: trialInfo.urgent ? '#BE123C' : 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                      color: 'white', whiteSpace: 'nowrap', marginLeft: '20px',
                      opacity: 0.7,
                    }}>
                      Upgrade — coming soon
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ── 2. Plan & Pricing ─────────────────────────────────────────
                Billing cycle toggle and plan card with pricing.
                TO ACTIVATE: Connect to Stripe Checkout when ready.
            ─────────────────────────────────────────────────────────────── */}
            <div style={cardStyle}>
              <h2 style={sectionTitle}>QuoteFlow Plan</h2>

              {/* Billing toggle */}
              <div style={{ display: 'flex', gap: '0', marginBottom: '24px', background: '#F1F5F9', borderRadius: '10px', padding: '4px', width: 'fit-content' }}>
                {['monthly', 'annual'].map(cycle => (
                  <button key={cycle} onClick={() => setBillingCycle(cycle)} style={{
                    padding: '7px 20px', borderRadius: '7px', fontSize: '13px',
                    fontWeight: '600', border: 'none', cursor: 'pointer',
                    background: billingCycle === cycle ? 'white' : 'transparent',
                    color: billingCycle === cycle ? '#0F172A' : '#64748B',
                    boxShadow: billingCycle === cycle ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s',
                  }}>
                    {cycle === 'monthly' ? 'Monthly' : 'Annual'}
                    {cycle === 'annual' && (
                      <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: '700', color: '#15803D', background: '#F0FDF4', padding: '1px 5px', borderRadius: '4px' }}>
                        SAVE 20%
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Plan card */}
              <div style={{
                border: '2px solid #2563EB', borderRadius: '12px',
                padding: '24px', background: '#FAFEFF',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', top: '12px', right: '12px',
                  background: '#2563EB', color: 'white', fontSize: '11px',
                  fontWeight: '700', padding: '3px 10px', borderRadius: '999px',
                }}>Most Popular</div>

                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: '0 0 4px' }}>
                    QuoteFlow Pro
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                    <span style={{ fontSize: '36px', fontWeight: '800', color: '#0F172A', letterSpacing: '-1px' }}>
                      {price.amount}
                    </span>
                    <span style={{ fontSize: '14px', color: '#64748B', fontWeight: '500' }}>{price.period}</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#94A3B8', margin: '4px 0 0' }}>{price.note}</p>
                </div>

                {/* Features list */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '24px' }}>
                  {planFeatures.map((feature, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0,
                        background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <svg width="8" height="7" viewBox="0 0 8 7" fill="none">
                          <path d="M1 3.5L3 5.5L7 1" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <span style={{ fontSize: '13px', color: '#374151' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                <button style={{
                  width: '100%', height: '44px', borderRadius: '10px',
                  background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                  color: 'white', fontSize: '14px', fontWeight: '700',
                  border: 'none', cursor: 'not-allowed', opacity: 0.6,
                }}>
                  Subscribe — Stripe coming soon
                </button>
              </div>
            </div>

            {/* ── 3. Connect Payment Methods ────────────────────────────────
                Allows contractors to connect Stripe or GoCardless to send
                payment requests to their own customers.
                TO ACTIVATE STRIPE: Add Stripe Connect OAuth flow here.
                TO ACTIVATE GOCARDLESS: Add GoCardless Connect flow here.
            ─────────────────────────────────────────────────────────────── */}
            <div style={cardStyle}>
              <h2 style={sectionTitle}>Accept Payments from Your Customers</h2>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 24px', lineHeight: '1.6' }}>
                Connect a payment provider to send payment requests directly from your quotes and invoices.
                Your customers can pay online and quote statuses will update automatically.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

                {/* Stripe */}
                <div style={{
                  border: '1.5px solid #E2E8F0', borderRadius: '12px',
                  padding: '24px', background: '#FAFAFA',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: '0 0 4px' }}>Stripe</p>
                      <p style={{ fontSize: '12px', color: '#64748B', margin: 0 }}>Credit & debit cards</p>
                    </div>
                    <span style={{
                      fontSize: '11px', fontWeight: '600', padding: '3px 8px',
                      borderRadius: '999px', background: '#F1F5F9', color: '#64748B',
                    }}>Not connected</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#94A3B8', margin: '0 0 16px', lineHeight: '1.5' }}>
                    Accept Visa, Mastercard, and more. Instant payouts available.
                  </p>
                  <button style={{
                    width: '100%', height: '38px', borderRadius: '8px',
                    background: '#635BFF', color: 'white', fontSize: '13px',
                    fontWeight: '600', border: 'none', cursor: 'not-allowed', opacity: 0.6,
                  }}>Connect Stripe — coming soon</button>
                </div>

                {/* GoCardless */}
                <div style={{
                  border: '1.5px solid #E2E8F0', borderRadius: '12px',
                  padding: '24px', background: '#FAFAFA',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: '0 0 4px' }}>GoCardless</p>
                      <p style={{ fontSize: '12px', color: '#64748B', margin: 0 }}>Direct debit (UK)</p>
                    </div>
                    <span style={{
                      fontSize: '11px', fontWeight: '600', padding: '3px 8px',
                      borderRadius: '999px', background: '#F1F5F9', color: '#64748B',
                    }}>Not connected</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#94A3B8', margin: '0 0 16px', lineHeight: '1.5' }}>
                    Bank-to-bank payments. Lower fees, popular with UK contractors.
                  </p>
                  <button style={{
                    width: '100%', height: '38px', borderRadius: '8px',
                    background: '#00A4D4', color: 'white', fontSize: '13px',
                    fontWeight: '600', border: 'none', cursor: 'not-allowed', opacity: 0.6,
                  }}>Connect GoCardless — coming soon</button>
                </div>
              </div>
            </div>

            {/* ── 4. Payment History ────────────────────────────────────────
                Placeholder until Stripe is connected.
                Once connected, list transactions here from Stripe API.
            ─────────────────────────────────────────────────────────────── */}
            <div style={cardStyle}>
              <h2 style={sectionTitle}>Billing History</h2>
              <div style={{
                padding: '40px', textAlign: 'center',
                border: '1.5px dashed #E2E8F0', borderRadius: '10px',
                background: '#FAFBFC',
              }}>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#94A3B8', margin: '0 0 6px' }}>
                  No billing history yet
                </p>
                <p style={{ fontSize: '13px', color: '#CBD5E1', margin: 0 }}>
                  Your invoices and payment history will appear here once Stripe is connected.
                </p>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  )
}